<div class="user-profile">
  <div class="user-profile__content">
    <div class="user-profile__layout">
      <div class="user-profile__header">
        <div class="user-profile__heading">
          <h1 class="page-title">Profile</h1>
          <p class="page-subtitle">Update your personal information</p>
        </div>
      </div>

      <app-card>
        <form [formGroup]="profileForm" (ngSubmit)="onSubmit()" class="user-profile__form">
          <div class="user-profile__form-field user-profile__form-field--full-width">
            <div class="user-profile__avatar-section">
              <app-avatar
                class="user-profile__avatar-preview"
                [imageUrl]="profileState.profile()?.imageUrl ?? authState.user()?.imageUrl ?? null"
                [name]="profileState.profile()?.name ?? authState.user()?.name ?? ''"
                size="2xl"></app-avatar>
              <div class="user-profile__avatar-details">
                <p class="user-profile__avatar-name">
                  {{ profileState.profile()?.name || authState.user()?.name || "Unknown user" }}
                </p>
                <p class="user-profile__avatar-meta">
                  {{ profileState.profile()?.email || authState.user()?.email || "No email available" }}
                </p>
                @if (authState.user()?.role?.name) {
                <p class="user-profile__avatar-meta">{{ authState.user()?.role?.name }}</p>
                }
              </div>
              <div class="user-profile__avatar-actions">
                <input type="file" #avatarInput accept="image/*" (change)="onAvatarChange($event)" style="display: none" />
                <app-button type="button" variant="secondary" (click)="avatarInput.click()"> Change Image </app-button>
                @if (authState.user()?.imageUrl) {
                <app-button type="button" variant="secondary" (click)="removeAvatar()"> Remove Image </app-button>
                }
              </div>
            </div>
          </div>

          <div class="user-profile__form-grid">
            <div class="user-profile__form-field user-profile__form-field--spaced">
              <div class="user-profile__form-label-row">
                <ng-glyph class="user-profile__form-label-icon" name="person"></ng-glyph>
                <label for="name" class="user-profile__form-label">Name</label>
              </div>
              <input
                id="name"
                type="text"
                formControlName="name"
                class="user-profile__form-input"
                [class.user-profile__form-input--error]="profileForm.get('name')?.invalid && profileForm.get('name')?.touched" />
              @if (profileForm.get('name')?.invalid && profileForm.get('name')?.touched) {
              <div class="user-profile__form-error">Name is required and must be at least 2 characters</div>
              }
            </div>

            <div class="user-profile__form-field user-profile__form-field--spaced">
              <div class="user-profile__form-label-row">
                <ng-glyph class="user-profile__form-label-icon" name="mail"></ng-glyph>
                <label for="email" class="user-profile__form-label">Email</label>
              </div>
              <input
                id="email"
                type="email"
                formControlName="email"
                class="user-profile__form-input"
                [class.user-profile__form-input--error]="profileForm.get('email')?.invalid && profileForm.get('email')?.touched" />
              @if (profileForm.get('email')?.invalid && profileForm.get('email')?.touched) {
              <div class="user-profile__form-error">A valid email is required</div>
              }
            </div>

            <div class="user-profile__form-field user-profile__form-field--spaced">
              <div class="user-profile__form-label-row">
                <ng-glyph class="user-profile__form-label-icon" name="call"></ng-glyph>
                <label for="phone" class="user-profile__form-label">Phone</label>
              </div>
              <input id="phone" type="tel" formControlName="phone" class="user-profile__form-input" />
            </div>

            <div class="user-profile__form-field user-profile__form-field--spaced">
              <div class="user-profile__form-label-row">
                <ng-glyph class="user-profile__form-label-icon" name="location_on"></ng-glyph>
                <label for="address" class="user-profile__form-label">Address</label>
              </div>
              <input id="address" type="text" formControlName="address" class="user-profile__form-input" />
            </div>
          </div>

          <div class="user-profile__form-actions">
            <app-button type="button" variant="secondary" (click)="onCancel()">Cancel</app-button>
            <app-button type="submit" [disabled]="profileForm.invalid || loading()"> Save Changes </app-button>
          </div>
        </form>
      </app-card>

      <app-card class="user-profile__password-card">
        <form [formGroup]="passwordForm" (ngSubmit)="onChangePassword()" class="user-profile__form">
          <div class="user-profile__section-header">
            <div>
              <h2 class="user-profile__section-title">Change password</h2>
              <p class="user-profile__section-hint">Use a new password with at least 8 characters.</p>
            </div>
          </div>

          <div class="user-profile__form-grid">
            <div class="user-profile__form-field user-profile__form-field--full-width user-profile__form-field--spaced">
              <div class="user-profile__form-label-row">
                <ng-glyph class="user-profile__form-label-icon" name="lock"></ng-glyph>
                <label for="currentPassword" class="user-profile__form-label">Current password</label>
              </div>
              <input
                id="currentPassword"
                type="password"
                formControlName="currentPassword"
                class="user-profile__form-input"
                [class.user-profile__form-input--error]="passwordForm.get('currentPassword')?.invalid && passwordForm.get('currentPassword')?.touched" />
              @if (passwordForm.get('currentPassword')?.invalid && passwordForm.get('currentPassword')?.touched) {
              <div class="user-profile__form-error">Current password is required.</div>
              }
            </div>

            <div class="user-profile__form-field user-profile__form-field--spaced">
              <div class="user-profile__form-label-row">
                <ng-glyph class="user-profile__form-label-icon" name="key"></ng-glyph>
                <label for="newPassword" class="user-profile__form-label">New password</label>
              </div>
              <input
                id="newPassword"
                type="password"
                formControlName="newPassword"
                class="user-profile__form-input"
                [class.user-profile__form-input--error]="passwordForm.get('newPassword')?.invalid && passwordForm.get('newPassword')?.touched" />
              @if (passwordForm.get('newPassword')?.invalid && passwordForm.get('newPassword')?.touched) {
              <div class="user-profile__form-error">New password must be at least 8 characters.</div>
              }
            </div>

            <div class="user-profile__form-field user-profile__form-field--spaced">
              <div class="user-profile__form-label-row">
                <ng-glyph class="user-profile__form-label-icon" name="key"></ng-glyph>
                <label for="confirmPassword" class="user-profile__form-label">Confirm password</label>
              </div>
              <input
                id="confirmPassword"
                type="password"
                formControlName="confirmPassword"
                class="user-profile__form-input"
                [class.user-profile__form-input--error]="passwordForm.hasError('passwordMismatch') && passwordForm.get('confirmPassword')?.touched" />
              @if (passwordForm.hasError('passwordMismatch') && passwordForm.get('confirmPassword')?.touched) {
              <div class="user-profile__form-error">Passwords do not match.</div>
              }
            </div>
          </div>

          <div class="user-profile__form-actions">
            <app-button type="submit" [disabled]="passwordForm.invalid || isPasswordSaving">
              {{ isPasswordSaving ? "Updating..." : "Update password" }}
            </app-button>
          </div>
        </form>
      </app-card>

      <app-card class="user-profile__permissions-card">
        <div class="user-profile__section-header">
          <div>
            <h2 class="user-profile__section-title">Permissions</h2>
            <p class="user-profile__section-hint">Permissions granted by your role.</p>
          </div>
          <div class="user-profile__header-actions">
            <div class="user-profile__search">
              <input
                class="user-profile__search-input"
                type="text"
                [formControl]="permissionSearch"
                placeholder="Search by module or action" />
            </div>
          </div>
        </div>

        @if (permissionGroups.length === 0) {
        <div class="user-profile__empty">No permissions match your search.</div>
        } @else {
        <div class="user-profile__modules">
          @for (group of permissionGroups; track group.module) {
          <div class="user-profile__module">
            <div class="user-profile__module-header">
              <div class="user-profile__module-meta">
                <h3 class="user-profile__module-title">{{ group.module }}</h3>
                <span class="user-profile__module-count">{{ group.permissions.length }} permissions</span>
              </div>
            </div>
            <div class="user-profile__permissions">
              @for (permission of group.permissions; track permission.id) {
              <label class="user-profile__permission">
                <input type="checkbox" [checked]="true" disabled />
                <span class="user-profile__permission-text">
                  <span class="user-profile__permission-action">{{ permission.action }}</span>
                  <span class="user-profile__permission-desc"
                    >{{ permission.description || (permission.module + ":" + permission.action) }}</span
                  >
                </span>
              </label>
              }
            </div>
          </div>
          }
        </div>
        }
      </app-card>
    </div>
  </div>
</div>
